import { useEffect, useState, useCallback } from 'react';
import { apiFetch } from '@/lib/api';
import { Button } from '@/components/ui/button';
import { Card } from '@/components/ui/card';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { SummaryMetricCard, MetricGrid } from '@/components/global/SummaryMetricCard';
import { DateRangePicker, useDateRange } from '@/components/finance';
import { BarChart3, DollarSign, ShoppingCart, TrendingUp, Download } from 'lucide-react';
import { ProductIdentity } from '@/components/products';
import { MachineLink } from '@/components/machines';
import { downloadCsv } from '@/lib/csvExport';

interface SalesReportItem {
  date: string;
  productId: string | null;
  productSku: string | null;
  productName: string | null;
  productImageUrl: string | null;
  machineId: string | null;
  machineName: string | null;
  venueId: string | null;
  venueName: string | null;
  quantity: number;
  revenue: number;
  refunds: number;
  netRevenue: number;
  productCost: number;
  grossProfit: number;
}

interface SalesReport {
  period: { startDate: string; endDate: string };
  items: SalesReportItem[];
  summary: {
    totalSales: number;
    totalRevenue: number;
    totalRefunds: number;
    totalNetRevenue: number;
    totalProductCost: number;
    totalGrossProfit: number;
  };
}

function formatCurrency(value: number): string {
  return new Intl.NumberFormat('en-GB', {
    style: 'currency',
    currency: 'GBP',
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  }).format(value);
}

export default function SalesReportPage() {
  const { dateRange, setDateRange } = useDateRange('L30D');
  const [report, setReport] = useState<SalesReport | null>(null);
  const [loading, setLoading] = useState(true);
  const [groupBy, setGroupBy] = useState<string>('day');

  const loadData = useCallback(async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams({
        startDate: dateRange.startDate.toISOString(),
        endDate: dateRange.endDate.toISOString(),
        groupBy,
      });

      const res = await apiFetch(`/api/v1/reports/sales?${params}`);
      if (res.ok) {
        const data = await res.json();
        setReport(data);
      }
    } catch (error) {
      console.error('Error loading sales report:', error);
    } finally {
      setLoading(false);
    }
  }, [dateRange, groupBy]);

  useEffect(() => {
    loadData();
  }, [loadData]);

  function getRowLabel(item: SalesReportItem): string {
    switch (groupBy) {
      case 'product':
        return item.productName || item.productSku || 'Unknown';
      case 'machine':
        return item.machineName || 'Unknown';
      case 'venue':
        return item.venueName || 'Unknown';
      default:
        return item.date;
    }
  }

  function exportCsv() {
    if (!report || report.items.length === 0) return;
    const groupLabel =
      groupBy === 'day' ? 'Date'
      : groupBy === 'product' ? 'Product'
      : groupBy === 'machine' ? 'Machine'
      : 'Venue';
    downloadCsv(
      `sales-by-${groupBy}-${new Date().toISOString().slice(0, 10)}.csv`,
      [
        groupLabel,
        ...(groupBy === 'product' ? ['SKU'] : []),
        'Quantity',
        'Revenue (GBP)',
        'Refunds (GBP)',
        'Net revenue (GBP)',
        'COGS (GBP)',
        'Gross profit (GBP)',
      ],
      report.items.map((item) => [
        getRowLabel(item),
        ...(groupBy === 'product' ? [item.productSku ?? ''] : []),
        item.quantity,
        item.revenue.toFixed(2),
        item.refunds.toFixed(2),
        item.netRevenue.toFixed(2),
        item.productCost.toFixed(2),
        item.grossProfit.toFixed(2),
      ])
    );
  }

  return (
    <div className="min-h-screen bg-muted/30">
      <div className="space-y-6">
        <div className="flex items-center justify-between mb-6">
          <h1 className="text-2xl font-semibold">Sales Report</h1>
          <div className="flex items-center gap-2">
            <DateRangePicker value={dateRange} onChange={setDateRange} />
            <Button
              variant="outline"
              onClick={exportCsv}
              disabled={loading || !report || report.items.length === 0}
            >
              <Download className="h-4 w-4 mr-2" />
              Export
            </Button>
          </div>
        </div>

        <MetricGrid className="mb-6">
          <SummaryMetricCard
            label="Total Sales"
            value={report ? report.summary.totalSales.toLocaleString() : '-'}
            icon={ShoppingCart}
            isLoading={loading}
          />
          <SummaryMetricCard
            label="Net Revenue"
            value={report ? formatCurrency(report.summary.totalNetRevenue) : '-'}
            icon={DollarSign}
            isLoading={loading}
          />
          <SummaryMetricCard
            label="Gross Profit"
            value={report ? formatCurrency(report.summary.totalGrossProfit) : '-'}
            icon={TrendingUp}
            isLoading={loading}
          />
          <SummaryMetricCard
            label="Refunds"
            value={report ? formatCurrency(report.summary.totalRefunds) : '-'}
            icon={BarChart3}
            isLoading={loading}
          />
        </MetricGrid>

        <Card>
          <div className="p-4 flex items-center gap-4 border-b">
            <span className="text-sm text-muted-foreground">Group by:</span>
            <Select value={groupBy} onValueChange={setGroupBy}>
              <SelectTrigger className="w-[180px]">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="day">Day</SelectItem>
                <SelectItem value="product">Product</SelectItem>
                <SelectItem value="machine">Machine</SelectItem>
                <SelectItem value="venue">Venue</SelectItem>
              </SelectContent>
            </Select>
          </div>

          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>{groupBy === 'day' ? 'Date' : groupBy.charAt(0).toUpperCase() + groupBy.slice(1)}</TableHead>
                <TableHead className="text-right">Quantity</TableHead>
                <TableHead className="text-right">Revenue</TableHead>
                <TableHead className="text-right">Refunds</TableHead>
                <TableHead className="text-right">Net Revenue</TableHead>
                <TableHead className="text-right">COGS</TableHead>
                <TableHead className="text-right">Gross Profit</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {loading ? (
                <TableRow>
                  <TableCell colSpan={7} className="text-center py-8 text-muted-foreground">
                    Loading...
                  </TableCell>
                </TableRow>
              ) : report?.items.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={7} className="text-center py-8 text-muted-foreground">
                    No sales found for this period
                  </TableCell>
                </TableRow>
              ) : (
                report?.items.map((item, idx) => (
                  <TableRow key={idx}>
                    <TableCell className="font-medium">
                      {groupBy === 'product' ? (
                        <ProductIdentity
                          productId={item.productId ?? undefined}
                          name={item.productName ?? item.productSku ?? 'Unknown'}
                          sku={item.productSku}
                          imageUrl={item.productImageUrl}
                          size="sm"
                        />
                      ) : groupBy === 'machine' && item.machineId ? (
                        <MachineLink machineId={item.machineId} name={item.machineName ?? 'Unknown'} />
                      ) : (
                        getRowLabel(item)
                      )}
                    </TableCell>
                    <TableCell className="text-right">{item.quantity.toLocaleString()}</TableCell>
                    <TableCell className="text-right">{formatCurrency(item.revenue)}</TableCell>
                    <TableCell className="text-right text-red-600">
                      {item.refunds > 0 ? `-${formatCurrency(item.refunds)}` : '-'}
                    </TableCell>
                    <TableCell className="text-right">{formatCurrency(item.netRevenue)}</TableCell>
                    <TableCell className="text-right">{formatCurrency(item.productCost)}</TableCell>
                    <TableCell className="text-right font-medium">{formatCurrency(item.grossProfit)}</TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </Card>
      </div>
    </div>
  );
}
